import { TagGroupDto } from "@closerplatform/spinner-openapi";
import { flow } from "lodash";
import React, { createContext, useContext, useEffect } from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../../models";
import { usePreloader } from "../../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithSelectables,
  Params
} from "../../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withSelectables
} from "../../models/useSettingsList/useSettingsList";
import { ExistingTagGroupFormType, NewTagGroupFormType } from "./types";
import {
  copyExistingTagGroupForm,
  createNewTagGroupForm,
  mapExistingFormToUpdateForm,
  mapFormToTagGroup,
  mapNewFormToCreateForm,
  mapTagGroupToForm,
  validateNewTagGroupNoNameConflict,
  validateNewTagGroupNoTagConflict,
  validateTagGroupsNoTagsConflict
} from "./utils";
import { useAgentServices } from "../../../../models/agentServices";

type HookParams = Params<ExistingTagGroupFormType, NewTagGroupFormType, TagGroupDto>;
type Hook = ListHookEditable<ExistingTagGroupFormType, NewTagGroupFormType>;
type HookSelectable = ListHookWithSelectables<ExistingTagGroupFormType, NewTagGroupFormType, Hook>;
export type TagGroupsProviderValue = HookSelectable;

const useListHook = flow<[HookParams], Hook, HookSelectable>(
  useSettingsListEditable,
  withSelectables
);

const useTagGroupsSettingsState = () => {
  const { setLoadingState } = usePreloader();
  const { uiStore, currentUserStore, tagGroupsStore, messageWidgetsStore } = useStore();
  const { spinnerService } = useAgentServices();

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const validateSubmit = (items: ExistingTagGroupFormType[]) => {
    if (!validateTagGroupsNoTagsConflict(items)) {
      uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.tagsNonUnique"));
      return false;
    }

    return true;
  };

  const validateNew = (newItem: NewTagGroupFormType, items: ExistingTagGroupFormType[]) => {
    if (!validateNewTagGroupNoTagConflict(newItem, items)) {
      uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.tagsNonUnique"));
      return false;
    }

    if (!validateNewTagGroupNoNameConflict(newItem, items)) {
      uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.nameNonUnique"));
      return false;
    }

    return true;
  };

  const updateEditedItems = (editedItems: ExistingTagGroupFormType[]) => {
    return spinnerService.updateTagGroups(
      editedItems.map((g) => mapFormToTagGroup(currentUserStore.orgId!, g))
    );
  };

  const listHook = useListHook({
    apiGetItems: () => spinnerService.getTagGroups(),
    apiCreateItem: (form) => spinnerService.createTagGroup(mapNewFormToCreateForm(form)),
    apiUpdateItem: (form) =>
      spinnerService.updateTagGroup(form.$.id.$, mapExistingFormToUpdateForm(form)),
    apiDeleteItem: (id) => spinnerService.deleteTagGroup(id),
    mapItemToForm: (group) =>
      mapTagGroupToForm(group, messageWidgetsStore.getOnlyAssignedWidgets(group.id)),
    copyExistingForm: copyExistingTagGroupForm,
    createNewForm: createNewTagGroupForm,
    saveErrorId: "settings.tagGroups.error.wrong",
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,
    onInitializeResult: setLoadingState,
    validateSubmit,
    validateNew,

    batchUpdateItems: updateEditedItems
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);

      item.$.active.onUpdate(setEdited);
      item.$.showInConversationDetails.onUpdate(setEdited);
      item.$.tagIds.onUpdate(setEdited);
      item.$.activeHours.onUpdate(setEdited);
      item.$.messageWidgets.onUpdate(setEdited);
    });
  }, [listHook.items]);

  useEffect(() => {
    tagGroupsStore.setTagGroups(
      listHook.initialItems.map((tg) => mapFormToTagGroup(currentUserStore.orgId!, tg))
    );
  }, [listHook.initialItems]);

  return listHook;
};

const TagGroupSettingsContext = createContext<HookSelectable | undefined>(undefined);

export const TagGroupSettingsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const hook = useTagGroupsSettingsState();

  return (
    <TagGroupSettingsContext.Provider value={hook}>{children}</TagGroupSettingsContext.Provider>
  );
};

export const useTagGroupsSettings = (): HookSelectable => {
  const context = useContext(TagGroupSettingsContext);

  if (context === undefined) {
    throw new Error("useTagGroupsSettings must be used within a TagGroupSettingsProvider");
  }

  return context as HookSelectable;
};
